import React, { useContext, useState, useEffect, useRef } from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    Paper,
    Typography,
    TableContainer,
    Tooltip,
    Switch,
    Box,
    Alert,
    Select,
    MenuItem,
    InputLabel,
    TablePagination,
    InputAdornment,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { ItemsContext } from "../App";
import { getApiToken } from "../authUtils";
import { useMsal } from "@azure/msal-react";
import DownloadBillpdf from "../components/DownloadBillpdf";
import { gstRate, withGst } from "../shared/gst";

const urlconfig = require('../config/urlconfig');

const CustomizationDashboard = () => {
    const { items, refreshItems } = useContext(ItemsContext);

    const { instance, accounts } = useMsal();
    const account = accounts && accounts.length > 0 ? accounts[0] : null;

    const [openDialog, setOpenDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [fileUrls, setFileUrls] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [customizationForms, setCustomizationForms] = useState([
        { modeOfCustomization: "", actualCustomizationCost: "", referenceCustomizationID: "", tripCustomizationDate: "", spocCustomizationName: "", customizationVendorName: "", customization: false, fileUrlCustomization: null }
    ]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const pdfCustomizationRef = useRef();
    const pdfCustomizationDownloadRef = useRef();


    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // 500ms debounce

        return () => clearTimeout(handler);
    }, [searchQuery]);

    // Fetch items when debounced search / page / rowsPerPage changes
    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            try {
                if (!mounted) return;
                setLoading(true);
                await refreshItems(debouncedSearchQuery, page, rowsPerPage);
            } catch (err) {
                console.error("Error refreshing items:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchData();
        return () => {
            mounted = false;
        };
    }, [debouncedSearchQuery, page, rowsPerPage, refreshItems]);

    const handleCustomizationAdd = (item) => {
        setSelectedItem(item);
        setOpenDialog(true);

        if (item.customizationDetails && item.customizationDetails.length > 0) {
            setCustomizationForms(
                item.customizationDetails.map(d => ({
                    modeOfCustomization: d.modeOfCustomization || "",
                    actualCustomizationCost: d.actualCustomizationCost || "",
                    customizationVendorName: d.customizationVendorName || "",
                    referenceCustomizationID: d.referenceCustomizationID || "",
                    tripCustomizationDate: d.tripCustomizationDate ? d.tripCustomizationDate.split("T")[0] : "",
                    spocCustomizationName: d.spocCustomizationName || "",
                    customization: !!d.customization,
                    fileUrlCustomization: d.fileUrlCustomization || null,
                    file: null
                }))
            );
        } else {
            setCustomizationForms([
                { modeOfCustomization: "", actualCustomizationCost: "", referenceCustomizationID: "", tripCustomizationDate: "", spocCustomizationName: "", customizationVendorName: "", customization: false, fileUrlCustomization: null, file: null }
            ]);
        }
    };

    const handleviewCustomization = (item) => {
        setSelectedItem(item);
        setFileUrls({}); // reset previously fetched urls
        setOpenViewDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setOpenViewDialog(false);
        setSelectedItem(null);
        setFileUrls({});
    };

    const handleAddAnotherCustomization = () => {
        setCustomizationForms([
            ...customizationForms,
            { modeOfCustomization: "", actualCustomizationCost: "", referenceCustomizationID: "", tripCustomizationDate: "", spocCustomizationName: "", customizationVendorName: "", customization: false, fileUrlCustomization: null, file: null }
        ]);
    };

    const handleFormChange = (index, field, value) => {
        const updatedForms = [...customizationForms];
        if (field === "file") {
            updatedForms[index]["file"] = value;
            updatedForms[index]["fileUrlCustomization"] = null;
        } else {
            updatedForms[index][field] = value;
        }
        setCustomizationForms(updatedForms);
    };

    const handleRemoveForm = (index) => {
        if (customizationForms[index].customization === true) {
            alert("Cannot remove the form, as customization is paid");
            return;
        }
        const updatedForms = customizationForms.filter((_, i) => i !== index);
        setCustomizationForms(updatedForms.length > 0 ? updatedForms : [{ modeOfCustomization: "", actualCustomizationCost: "", referenceCustomizationID: "", tripCustomizationDate: "", spocCustomizationName: "", customization: false, fileUrlCustomization: null, file: null }]);
    };

    const sendAuthenticatedRequest = async (url, method, body) => {
        try {
            const accessToken = await getApiToken(instance, account);
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: body ? JSON.stringify(body) : undefined,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    message: response.statusText,
                }));
                throw new Error(`API Error: ${response.status} - ${errorData.message || "Unknown error"}`);
            }

            const contentType = response.headers.get("content-type");
            return contentType?.includes("application/json") ? response.json() : response.text();
        } catch (error) {
            console.error("Authenticated API call failed:", error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;

        try {
            const accessToken = await getApiToken(instance, account);

            const currentDetails = selectedItem.customizationDetails || [];

            const updatedCustomizationDetails = await Promise.all(
                customizationForms.map(async (formData, index) => {
                    let fileUrlCustomization = formData.fileUrlCustomization || currentDetails[index]?.fileUrlCustomization || null;
                    if (formData.file) {
                        const uploadData = new FormData();
                        uploadData.append("file", formData.file);
                        const res = await fetch(
                            `${urlconfig.backend_api_base_url}/api/v2/customization/${selectedItem.id}/upload`,
                            {
                                method: "POST",
                                headers: { Authorization: `Bearer ${accessToken}` },
                                body: uploadData,
                            }
                        );
                        if (!res.ok) throw new Error("File upload failed");
                        fileUrlCustomization = await res.text();
                    }

                    return {
                        modeOfCustomization: (formData.modeOfCustomization || "").trim(),
                        customizationVendorName: (formData.customizationVendorName || "").trim(),
                        actualCustomizationCost: Number(formData.actualCustomizationCost) || 0,
                        referenceCustomizationID: formData.referenceCustomizationID ? formData.referenceCustomizationID.trim() : undefined,
                        tripCustomizationDate: formData.tripCustomizationDate || undefined,
                        spocCustomizationName: (formData.spocCustomizationName || "").trim(),
                        customization: !!formData.customization,
                        fileUrlCustomization,
                    };
                })
            );

            await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${selectedItem.id || selectedItem._id}`,
                "PUT",
                { customizationDetails: updatedCustomizationDetails }
            );

            // refresh with current search/page/size
            await refreshItems(debouncedSearchQuery, page, rowsPerPage);

            setSelectedItem(null);
            setOpenDialog(false);
            setCustomizationForms([{ modeOfCustomization: "", actualCustomizationCost: "", referenceCustomizationID: "", tripCustomizationDate: "", customizationVendorName: "", spocCustomizationName: "", customization: false, fileUrlCustomization: null, file: null }]);
        } catch (err) {
            console.error("Update failed:", err);
            alert("Failed to save customization details.");
        }
    };

    // Fetch bill URLs when opening view dialog
    useEffect(() => {
        if (openViewDialog && selectedItem?.customizationDetails) {
            const fetchUrls = async () => {
                const newUrls = {};
                try {
                    const accessToken = await getApiToken(instance, account);
                    for (const d of selectedItem.customizationDetails) {
                        if (d.fileUrlCustomization) {
                            try {
                                const res = await fetch(
                                    `${urlconfig.backend_api_base_url}/api/v2/customization/${selectedItem.id}/bill?key=${encodeURIComponent(d.fileUrlCustomization)}`,
                                    {
                                        method: "GET",
                                        headers: {
                                            "Content-Type": "application/json",
                                            Authorization: `Bearer ${accessToken}`,
                                        },
                                    }
                                );

                                if (!res.ok) throw new Error("Failed to fetch file URL");

                                const data = await res.json();
                                newUrls[d.fileUrlCustomization] = data.url;
                            } catch (error) {
                                console.error("Error fetching file URL for", d.fileUrlCustomization, error);
                            }
                        }
                    }
                } catch (err) {
                    console.error("Error getting access token for file fetch", err);
                } finally {
                    setFileUrls(newUrls);
                }
            };

            fetchUrls();
        } else {
            // reset fileUrls when closed
            setFileUrls({});
        }
    }, [openViewDialog, selectedItem, instance, account]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    return (
        <>
            <h1>Customization Dashboard</h1>
            <p>Manage customization details for sales orders.</p>

            <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Sales Order No, Client Name, SPOC, Details..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => {
                                    setSearchQuery('');
                                    setDebouncedSearchQuery('');
                                    setPage(0);
                                }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Paper sx={{ width: '100%', mt: 2 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table stickyHeader aria-label="customization table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Creation Date</TableCell>
                                    <TableCell>SO No.<br />Created By</TableCell>
                                    <TableCell>Budgeted Customization Cost<br />incl. GST (₹)</TableCell>
                                    <TableCell>Actual Customization Cost<br />incl. GST (₹)</TableCell>
                                    <TableCell>Variance (₹)</TableCell>
                                    <TableCell>Variance (%)</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.content && items.content.length > 0 ? (
                                    items.content.map((item, index) => {
                                        const gstPercent = gstRate(item, 'customizationGST');
                                        // Vendor bills already include GST, so the budget gets GST added and
                                        // the variance compares like with like
                                        const budgetedCost = withGst(item.customizationTotalPp, gstPercent);
                                        const actualCost = item.customizationDetails?.reduce(
                                            (sum, d) => sum + (Number(d.actualCustomizationCost) || 0),
                                            0
                                        ) || 0;
                                        const variance = budgetedCost - actualCost;
                                        const variancePercent =
                                            budgetedCost === 0
                                                ? actualCost === 0
                                                    ? "0%"
                                                    : "-100%"
                                                : ((variance / budgetedCost) * 100).toFixed(2) + "%";

                                        return (
                                            <TableRow key={index}>
                                                <TableCell>{item.createdAT ? new Date(item.createdAT).toLocaleDateString() : "N/A"}</TableCell>
                                                <TableCell>
                                                    {item.salesOrderNo || "N/A"}
                                                    <div>
                                                        <Typography fontSize="small" fontWeight="bold">{item.createdBy || "N/A"}</Typography>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    ₹{budgetedCost.toFixed(2)}
                                                    <Typography variant="caption" display="block" color="text.secondary">GST {gstPercent}%</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {item.customizationDetails?.length > 0 ? (
                                                        <Typography fontWeight="bold" color="success.main">
                                                            ₹{actualCost.toFixed(2)}
                                                        </Typography>
                                                    ) : (
                                                        "0.00"
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ color: variance > 0 ? 'green' : variance < 0 ? 'red' : 'inherit', fontWeight: variance !== 0 ? 'bold' : 'normal' }}>
                                                    ₹{variance.toFixed(2)}
                                                </TableCell>
                                                <TableCell>{variancePercent}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1}>
                                                        <Button variant="contained" onClick={() => handleCustomizationAdd(item)}>Edit</Button>
                                                        <Button variant="outlined" onClick={() => handleviewCustomization(item)}>View</Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Alert severity="info">
                                                {searchQuery
                                                    ? "No matching sales orders found for your search."
                                                    : "No sales orders available."}
                                            </Alert>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={items.totalElements || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Customization Dialog */}
            <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Add Customization {selectedItem?.salesOrderNo && `for ${selectedItem.salesOrderNo}`}</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit}>
                        {customizationForms.map((form, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, position: "relative", backgroundColor: '#f5f5f5' }} elevation={3}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveForm(index)}
                                    sx={{ position: "absolute", top: 5, right: 5, color: "red", fontSize: "16px" }}
                                    disabled={customizationForms.length === 1}
                                >
                                    <CloseIcon />
                                </IconButton>

                                <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <Grid item>Unpaid</Grid>
                                    <Grid item>
                                        <Tooltip
                                            title={form.customization ? "Once changed to paid, cannot be reverted" : ""}
                                            arrow
                                        >
                                            <span>
                                                <Switch
                                                    checked={form.customization}
                                                    onChange={() => {
                                                        if (!form.customization) {
                                                            handleFormChange(index, "customization", true);
                                                        }
                                                    }}
                                                    disabled={form.customization}
                                                />
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>Paid</Grid>
                                </Grid>

                                <InputLabel id={`mode-label-${index}`}>Mode of Customization</InputLabel>
                                <Select
                                    labelId={`mode-label-${index}`}
                                    value={form.modeOfCustomization}
                                    fullWidth
                                    onChange={(e) => handleFormChange(index, "modeOfCustomization", e.target.value)}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <em>Select Mode of Customization</em>;
                                        }
                                        return selected;
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        <em>Select Mode of Customization</em>
                                    </MenuItem>
                                    <MenuItem value="Screen-Printing">Screen Printing</MenuItem>
                                    <MenuItem value="Embroidery">Embroidery</MenuItem>
                                    <MenuItem value="Sublimation">Sublimation</MenuItem>
                                    <MenuItem value="DTF">DTF</MenuItem>
                                    <MenuItem value="Engraving">Engraving</MenuItem>
                                    <MenuItem value="UV Printing">UV Printing</MenuItem>
                                    <MenuItem value="UV DTF">UV DTF</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>

                                <TextField
                                    label="Invoice No. or Any Other"
                                    fullWidth
                                    type="text"
                                    margin="normal"
                                    required
                                    value={form.referenceCustomizationID}
                                    onChange={(e) => handleFormChange(index, "referenceCustomizationID", e.target.value)}
                                />
                                <TextField
                                    label="Vendor Name"
                                    fullWidth
                                    type="text"
                                    margin="normal"
                                    required
                                    value={form.customizationVendorName}
                                    onChange={(e) => handleFormChange(index, "customizationVendorName", e.target.value)}
                                />
                                <TextField
                                    label="Actual Customization Cost (₹)"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={form.actualCustomizationCost}
                                    onChange={(e) => handleFormChange(index, "actualCustomizationCost", e.target.value)}
                                />
                                <TextField
                                    label="Customization Date/ Transaction Date/ Invoice Date"
                                    type="date"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={form.tripCustomizationDate}
                                    onChange={(e) => handleFormChange(index, "tripCustomizationDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="SPOC Name"
                                    type="text"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={form.spocCustomizationName}
                                    onChange={(e) => handleFormChange(index, "spocCustomizationName", e.target.value)}
                                />

                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: "bold", color: "blue" }}>
                                    Upload Customization Bill:
                                </Typography>
                                <TextField
                                    type="file"
                                    inputProps={{ accept: "image/*" }}
                                    fullWidth
                                    margin="normal"
                                    onChange={(e) => handleFormChange(index, "file", e.target.files[0])}
                                />
                                {form.file && (
                                    <Box mt={1}>
                                        <Typography variant="body2">Selected File: {form.file.name}</Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))}

                        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
                            <Grid item>
                                <Button variant="contained" onClick={handleAddAnotherCustomization}>
                                    Add Another Customization
                                </Button>
                            </Grid>
                        </Grid>

                        <DialogActions>
                            <Button onClick={handleClose} color="secondary">Cancel</Button>
                            <Button type="submit" variant="contained" color="primary">Submit</Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View Customization Dialog */}
            <Dialog open={openViewDialog} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    <Typography variant="h6">
                        Customization Details for {selectedItem?.salesOrderNo || "N/A"}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedItem?.customizationDetails && selectedItem.customizationDetails.length > 0 ? (
                        selectedItem.customizationDetails.map((d, index) => (
                            <Paper key={index} elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                                <Typography><strong>Mode of Customization:</strong> {d.modeOfCustomization || "N/A"}</Typography>
                                <Typography><strong>Vendor Name:</strong> {d.customizationVendorName || "N/A"}</Typography>
                                <Typography><strong>Invoice No./ Any Other:</strong> {d.referenceCustomizationID || "N/A"}</Typography>
                                <Typography><strong>Actual Customization Cost (₹):</strong> {d.actualCustomizationCost || "N/A"}</Typography>
                                <Typography><strong>Customization Date/ Transaction Date/ Invoice Date:</strong> {d.tripCustomizationDate?.split("T")[0] || "N/A"}</Typography>
                                <Typography><strong>SPOC Name:</strong> {d.spocCustomizationName || "N/A"}</Typography>

                                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: "bold", color: "blue" }}>
                                    Customization Bill:
                                </Typography>

                                {d.fileUrlCustomization ? (
                                    fileUrls[d.fileUrlCustomization] ? (
                                        <img
                                            src={fileUrls[d.fileUrlCustomization]}
                                            alt="Customization Bill"
                                            crossOrigin="anonymous"
                                            style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <Typography variant="body2" sx={{ color: "orange" }}>
                                            Loading...
                                        </Typography>
                                    )
                                ) : (
                                    <Typography variant="body2" sx={{ color: "orange" }}>
                                        No bill uploaded
                                    </Typography>
                                )}

                                <Typography>
                                    <strong>Customization:</strong>{" "}
                                    {d.customization === true ? (
                                        <Typography component="span" sx={{ color: "green", fontWeight: "bold" }}>
                                            Paid
                                        </Typography>
                                    ) : (
                                        <Typography component="span" sx={{ color: "red", fontWeight: "bold" }}>
                                            Unpaid
                                        </Typography>
                                    )}
                                </Typography>
                            </Paper>
                        ))
                    ) : (
                        <Typography>No customization details available.</Typography>
                    )}
                </DialogContent>

                {selectedItem?.customizationDetails && selectedItem.customizationDetails.length > 0 && (
                    <Typography sx={{ p: 2, fontWeight: "bold", color: "green", textAlign: "right" }}>
                        Total Customization Cost: ₹
                        {selectedItem.customizationDetails.reduce((sum, d) => sum + (Number(d.actualCustomizationCost) || 0), 0).toFixed(2)} (incl. GST)
                    </Typography>
                )}

                    <DownloadBillpdf
                                ref={pdfCustomizationDownloadRef}
                                pdfContentRef={pdfCustomizationRef}
                                fileName={`Customization_Details_${selectedItem?.salesOrderNo}.pdf`}
                                />
                <DialogActions>
                    <Button variant="contained" color="success" onClick={() => pdfCustomizationDownloadRef.current?.downloadPDF()}
                        disabled={!selectedItem?.customizationDetails?.length}>
                            Download Customization Details
                    </Button>
                    <Button onClick={handleClose} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/**Download Customization detail PDF UI */}
                <div style={{ position: "absolute", top: "-9999px", left: "-9999px", opacity: 0, pointerEvents: "none",}}>
                    <div ref={pdfCustomizationRef} style={{ width: "600px", padding: "20px" }}>
                        <h2>Customization Details</h2>
                        <p><strong>Sales Order:</strong> {selectedItem?.salesOrderNo || "N/A"}</p>
                        <hr />

                        {selectedItem?.customizationDetails?.length > 0 &&
                        selectedItem.customizationDetails.map((d, index) => (
                            <div key={index} style={{ marginBottom: "25px" }}>
                            <p><strong>Mode of Customization:</strong> {d.modeOfCustomization || "N/A"}</p>
                            <p><strong>Vendor Name:</strong> {d.customizationVendorName || "N/A"}</p>
                            <p><strong>Invoice No. / Any Other:</strong> {d.referenceCustomizationID || "N/A"}</p>
                            <p><strong>Actual Customization Cost:</strong> ₹{d.actualCustomizationCost || "N/A"}</p>
                            <p><strong>Customization Date:</strong> {d.tripCustomizationDate?.split("T")[0] || "N/A"}</p>
                            <p><strong>SPOC Name:</strong> {d.spocCustomizationName || "N/A"}</p>

                            {d.fileUrlCustomization && fileUrls[d.fileUrlCustomization] && (
                                <img
                                src={fileUrls[d.fileUrlCustomization]}
                                alt="Customization Bill"
                                style={{
                                    width: "100%",
                                    marginTop: "10px",
                                    borderRadius: "6px",
                                    border: "1px solid #ccc",
                                }}
                                />
                            )}

                            <p>
                                <strong>Customization:</strong>{" "}
                                <span style={{ color: d.customization ? "green" : "red", fontWeight: "bold" }}>
                                {d.customization ? "Paid" : "Unpaid"}
                                </span>
                            </p>

                            <hr />
                            </div>
                        ))}

                        {selectedItem?.customizationDetails?.length > 0 && (
                        <h3 style={{ textAlign: "right", marginTop: "20px" }}>
                            Total Customization Cost: ₹
                            {selectedItem.customizationDetails
                            .reduce((sum, d) => sum + (Number(d.actualCustomizationCost) || 0), 0)
                            .toFixed(2)} (incl. GST)
                        </h3>
                        )}
                    </div>
                    </div>

        </>
    );
};

export default CustomizationDashboard;
